const makeTiptap = (nodes) => JSON.stringify({ type: 'doc', content: nodes });

const h2 = (text) => ({ type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text }] });
const p = (...parts) => ({ type: 'paragraph', content: parts.map(part => typeof part === 'string' ? { type: 'text', text: part } : part) });
const bold = (text) => ({ type: 'text', marks: [{ type: 'bold' }], text });
const italic = (text) => ({ type: 'text', marks: [{ type: 'italic' }], text });

const abcd = (title, level, questions) => ({
  type: 'abcdExercise',
  attrs: { title, level, exerciseId: null, questions },
});

const trueFalse = (title, level, statements) => ({
  type: 'trueFalseExercise',
  attrs: { title, level, exerciseId: null, alwaysShowAnswers: false, statements },
});

const matching = (title, level, sentences) => ({
  type: 'matchingHalves',
  attrs: { title, level, exerciseId: null, sentences },
});

// ─── TEMPLATE 1: Present Simple (A1) ─────────────────────────────────────────

const presentSimpleContent = makeTiptap([
  h2('The Present Simple'),
  p(
    'We use the Present Simple for habits, routines, and facts. For ',
    bold('he / she / it'),
    ' we add ',
    bold('-s'),
    ' to the verb.',
  ),
  p(italic('I work. She works. They work. He doesn\'t work on weekends.')),
  { type: 'paragraph', content: [] },
  abcd('Present Simple: Choose the correct form', 'A1', [
    {
      id: 'q1',
      text: 'She ___ to school every day.',
      options: [
        { id: 'a', text: 'go', isCorrect: false },
        { id: 'b', text: 'goes', isCorrect: true },
        { id: 'c', text: 'going', isCorrect: false },
        { id: 'd', text: 'goed', isCorrect: false },
      ],
    },
    {
      id: 'q2',
      text: 'Do you ___ coffee in the morning?',
      options: [
        { id: 'a', text: 'drink', isCorrect: true },
        { id: 'b', text: 'drinks', isCorrect: false },
        { id: 'c', text: 'drank', isCorrect: false },
        { id: 'd', text: 'drinking', isCorrect: false },
      ],
    },
    {
      id: 'q3',
      text: 'He ___ not like vegetables.',
      options: [
        { id: 'a', text: 'is', isCorrect: false },
        { id: 'b', text: 'are', isCorrect: false },
        { id: 'c', text: 'does', isCorrect: true },
        { id: 'd', text: 'do', isCorrect: false },
      ],
    },
  ]),
  { type: 'paragraph', content: [] },
  trueFalse('True or False: Present Simple Rules', 'A1', [
    {
      id: 's1',
      text: 'We add -s to the verb after he, she, or it.',
      answer: true,
      explanation: 'Correct! For third person singular we add -s: she works, he reads.',
    },
    {
      id: 's2',
      text: 'We use Present Simple for actions happening right now.',
      answer: false,
      explanation: 'We use Present Continuous for actions happening right now.',
    },
    {
      id: 's3',
      text: 'The negative form uses don\'t or doesn\'t.',
      answer: true,
      explanation: 'Correct! I/you/we/they → don\'t; he/she/it → doesn\'t.',
    },
  ]),
]);

// ─── TEMPLATE 2: Daily Routines (A2) ──────────────────────────────────────────

const dailyRoutinesContent = makeTiptap([
  h2('Daily Routines'),
  p('Look at Maria\'s day. She is a nurse and has a very busy schedule.'),
  p(
    bold('6:30'),
    ' — wakes up and has a shower. ',
    bold('7:00'),
    ' — eats breakfast. ',
    bold('8:00'),
    ' — leaves for work. ',
    bold('14:00'),
    ' — has lunch with colleagues. ',
    bold('20:00'),
    ' — cooks dinner and relaxes.',
  ),
  { type: 'paragraph', content: [] },
  matching('Match the activity to the time', 'A2', [
    { id: 'p1', left: 'Wakes up and has a shower', right: '6:30 in the morning' },
    { id: 'p2', left: 'Leaves for work', right: '8:00 in the morning' },
    { id: 'p3', left: 'Has lunch with colleagues', right: '2:00 in the afternoon' },
    { id: 'p4', left: 'Cooks dinner and relaxes', right: '8:00 in the evening' },
    { id: 'p5', left: 'Eats breakfast', right: '7:00 in the morning' },
  ]),
  { type: 'paragraph', content: [] },
  abcd('Daily Routines Vocabulary', 'A2', [
    {
      id: 'q1',
      text: 'She ___ breakfast at 7 o\'clock.',
      options: [
        { id: 'a', text: 'makes', isCorrect: false },
        { id: 'b', text: 'has', isCorrect: true },
        { id: 'c', text: 'takes', isCorrect: false },
        { id: 'd', text: 'does', isCorrect: false },
      ],
    },
    {
      id: 'q2',
      text: 'What time does she ___ for work?',
      options: [
        { id: 'a', text: 'go', isCorrect: false },
        { id: 'b', text: 'come', isCorrect: false },
        { id: 'c', text: 'leave', isCorrect: true },
        { id: 'd', text: 'start', isCorrect: false },
      ],
    },
    {
      id: 'q3',
      text: 'She has a very ___ schedule.',
      options: [
        { id: 'a', text: 'slow', isCorrect: false },
        { id: 'b', text: 'easy', isCorrect: false },
        { id: 'c', text: 'free', isCorrect: false },
        { id: 'd', text: 'busy', isCorrect: true },
      ],
    },
  ]),
]);

// ─── TEMPLATE 3: Job Interview Skills (B1) ────────────────────────────────────

const jobInterviewContent = makeTiptap([
  h2('Job Interview Skills'),
  p(
    'A job interview is your chance to show an employer why you are the right person for the job. ',
    'Good preparation is essential. Research the company, practise common questions, and dress professionally.',
  ),
  p(
    bold('Common questions: '),
    italic('"Tell me about yourself." / "What are your strengths?" / "Why do you want this job?"'),
  ),
  { type: 'paragraph', content: [] },
  trueFalse('Interview Tips: True or False?', 'B1', [
    {
      id: 's1',
      text: 'You should arrive 10–15 minutes early for a job interview.',
      answer: true,
      explanation: 'Arriving early shows punctuality and gives you time to calm your nerves.',
    },
    {
      id: 's2',
      text: 'It is fine to say negative things about your previous employer.',
      answer: false,
      explanation: 'Speaking negatively about previous employers looks unprofessional.',
    },
    {
      id: 's3',
      text: 'Asking questions at the end of an interview is a good sign.',
      answer: true,
      explanation: 'It shows you are interested in the company and the role.',
    },
    {
      id: 's4',
      text: 'You should memorise your CV word for word.',
      answer: false,
      explanation: 'Speak naturally about your experience — interviewers want a conversation.',
    },
  ]),
  { type: 'paragraph', content: [] },
  abcd('Job Interview Vocabulary', 'B1', [
    {
      id: 'q1',
      text: 'She applied ___ the marketing position.',
      options: [
        { id: 'a', text: 'to', isCorrect: false },
        { id: 'b', text: 'for', isCorrect: true },
        { id: 'c', text: 'at', isCorrect: false },
        { id: 'd', text: 'in', isCorrect: false },
      ],
    },
    {
      id: 'q2',
      text: 'His ___ includes five years at a tech company.',
      options: [
        { id: 'a', text: 'hobby', isCorrect: false },
        { id: 'b', text: 'education', isCorrect: false },
        { id: 'c', text: 'experience', isCorrect: true },
        { id: 'd', text: 'appearance', isCorrect: false },
      ],
    },
    {
      id: 'q3',
      text: 'Can you ___ your availability for the role?',
      options: [
        { id: 'a', text: 'tell', isCorrect: false },
        { id: 'b', text: 'say', isCorrect: false },
        { id: 'c', text: 'confirm', isCorrect: true },
        { id: 'd', text: 'speak', isCorrect: false },
      ],
    },
  ]),
]);

// ─── TEMPLATE 4: Environment & Climate (B2) ───────────────────────────────────

const climateContent = makeTiptap([
  h2('The Climate Crisis'),
  p(
    'Global temperatures have risen by approximately 1.1°C since pre-industrial times, ',
    'and scientists warn that exceeding 1.5°C could trigger irreversible tipping points. ',
    'Extreme weather events — droughts, floods, heatwaves — are becoming more frequent and intense.',
  ),
  p(
    bold('Key causes: '),
    'burning fossil fuels, deforestation, industrial agriculture, and transport emissions.',
  ),
  { type: 'paragraph', content: [] },
  trueFalse('Reading Comprehension: Climate Change', 'B2', [
    {
      id: 's1',
      text: 'Global temperatures have risen by more than 2°C since pre-industrial times.',
      answer: false,
      explanation: 'The text says temperatures have risen by approximately 1.1°C.',
    },
    {
      id: 's2',
      text: 'Scientists consider 1.5°C a critical threshold for climate change.',
      answer: true,
      explanation: 'Exceeding 1.5°C could trigger irreversible tipping points.',
    },
    {
      id: 's3',
      text: 'Deforestation is listed as one of the key causes of climate change.',
      answer: true,
      explanation: 'The text explicitly lists deforestation among the key causes.',
    },
  ]),
  { type: 'paragraph', content: [] },
  abcd('Vocabulary in Context', 'B2', [
    {
      id: 'q1',
      text: 'A "tipping point" in climate science refers to a ___ that triggers larger changes.',
      options: [
        { id: 'a', text: 'slight variation', isCorrect: false },
        { id: 'b', text: 'critical threshold', isCorrect: true },
        { id: 'c', text: 'temporary fluctuation', isCorrect: false },
        { id: 'd', text: 'minor setback', isCorrect: false },
      ],
    },
    {
      id: 'q2',
      text: '"Irreversible" most closely means ___.',
      options: [
        { id: 'a', text: 'gradual', isCorrect: false },
        { id: 'b', text: 'dangerous', isCorrect: false },
        { id: 'c', text: 'cannot be undone', isCorrect: true },
        { id: 'd', text: 'unexpected', isCorrect: false },
      ],
    },
    {
      id: 'q3',
      text: 'The word "emissions" refers to ___.',
      options: [
        { id: 'a', text: 'natural disasters', isCorrect: false },
        { id: 'b', text: 'greenhouse gases released into the atmosphere', isCorrect: true },
        { id: 'c', text: 'laws protecting the environment', isCorrect: false },
        { id: 'd', text: 'energy consumption rates', isCorrect: false },
      ],
    },
  ]),
]);

// ─── Export ───────────────────────────────────────────────────────────────────

export const TEMPLATE_LESSONS = [
  {
    id: 'tpl-present-simple',
    title: 'Present Simple',
    level: 'A1',
    tag: 'Grammar',
    icon: '✏️',
    gradient: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #4c1d95 100%)',
    accentColor: 'rgba(167, 139, 250, 0.25)',
    description: 'Habits, routines, and facts. Grammar quiz and True/False rules check.',
    exerciseTypes: ['Multiple Choice', 'True / False'],
    content: presentSimpleContent,
  },
  {
    id: 'tpl-daily-routines',
    title: 'Daily Routines',
    level: 'A2',
    tag: 'Vocabulary',
    icon: '☀️',
    gradient: 'linear-gradient(135deg, #022c22 0%, #065f46 60%, #047857 100%)',
    accentColor: 'rgba(52, 211, 153, 0.2)',
    description: 'Everyday activities and time expressions. Matching exercise and vocabulary quiz.',
    exerciseTypes: ['Matching Halves', 'Multiple Choice'],
    content: dailyRoutinesContent,
  },
  {
    id: 'tpl-job-interview',
    title: 'Job Interview Skills',
    level: 'B1',
    tag: 'Speaking',
    icon: '💼',
    gradient: 'linear-gradient(135deg, #1c0a00 0%, #7c2d12 60%, #9a3412 100%)',
    accentColor: 'rgba(251, 191, 36, 0.2)',
    description: 'Preparation tips, common questions, and vocabulary for job seekers.',
    exerciseTypes: ['True / False', 'Multiple Choice'],
    content: jobInterviewContent,
  },
  {
    id: 'tpl-climate',
    title: 'The Climate Crisis',
    level: 'B2',
    tag: 'Reading',
    icon: '🌍',
    gradient: 'linear-gradient(135deg, #0c1445 0%, #1e3a5f 60%, #1a4a6b 100%)',
    accentColor: 'rgba(96, 165, 250, 0.2)',
    description: 'Reading passage on climate change with comprehension and vocabulary exercises.',
    exerciseTypes: ['True / False', 'Multiple Choice'],
    content: climateContent,
  },
];
